import type { RouteLocationNormalizedLoaded } from 'vue-router';

import type { LayoutSolutionAgencyConfiguration, LayoutSolutionRequest } from '@/types';

type QueryType = RouteLocationNormalizedLoaded['query'];

export const isLayoutSolutionActive = (query: QueryType): boolean =>
  query != null && ['l', 'a', 'incsubs'].some((param) => param in query);

export const HOMEGATE_AGENCY = 'default';

export const getLayoutSolutionRequest = (query: QueryType): LayoutSolutionRequest | undefined => {
  if (isLayoutSolutionActive(query)) {
    const showSearchBarFromQuery = query.sb === '1';

    return {
      agency: (query.a || HOMEGATE_AGENCY) as string,
      layout: (query.layout || 'neutral') as string,
      includeSubAgencies: query.incsubs === '1',
      withSearchbar: !['0', '1'].includes(query.sb as string) ? undefined : showSearchBarFromQuery,
      primaryColor: (query.pc || '') as string,
      primaryTextColor: (query.ptc || '') as string,
    };
  }
};

export function getLayoutSolutionRequestQueryParameters(query: QueryType): Record<string, string> {
  const request = getLayoutSolutionRequest(query);

  if (!request) {
    return {};
  }

  return {
    l: request.layout,
    ...(request.agency !== HOMEGATE_AGENCY && { a: request.agency }),
    ...(request.includeSubAgencies !== undefined && {
      incsubs: request.includeSubAgencies ? '1' : '0',
    }),
    ...(request.withSearchbar !== undefined && { sb: request.withSearchbar ? '1' : '0' }),
    ...(request.primaryColor && { pc: request.primaryColor }),
    ...(request.primaryTextColor && { ptc: request.primaryTextColor }),
  };
}

export const getLayoutSolutionAgencyConfiguration = (
  configurations: Record<string, string[]> | Record<string, LayoutSolutionAgencyConfiguration>,
  agency: string,
): LayoutSolutionAgencyConfiguration | undefined => {
  const config = configurations[agency];

  if (!config) {
    return undefined;
  }

  // New format: direct array of sub-agencies
  if (Array.isArray(config)) {
    return {
      agency,
      showSearchBar: true, // Default value for new format
      subAgencies: config,
    };
  }

  // Old format: object with agency, showSearchBar, and subAgencies
  return config;
};

export function isValidCssHexColor(color: string | undefined): boolean {
  if (!color) {
    return false;
  }

  return (color.length === 6 || color.length === 3) && color.match(/^[0-9A-Fa-f]+$/) !== null;
}
